import { Video, SearchResult, ClipResult, TranscriptSegment } from '@/types';

// Backend API base URL
const BACKEND_BASE_URL = 'https://clip-sense-platform-380511447595.us-central1.run.app/api/v1';

// Video Search API types
export interface VideoIndexRequest {
  video_id?: string;
  frame_interval?: number;
  enable_diarization?: boolean;
}

export interface VideoIndexResponse {
  success: boolean;
  video_id: string;
  duration: number;
  frames_count: number;
  segments_count: number;
  speakers: string[];
  objects_detected: string[];
  entities: Record<string, string[]>;
  message: string;
}

export interface SearchRequest {
  query: string;
  video_id?: string;
  search_type?: 'text' | 'visual' | 'timestamp' | 'objects' | 'speaker';
  top_k?: number;
  speaker_filter?: string;
  object_filter?: string[];
  timestamp?: number;
  use_llm?: boolean;
  rerank?: boolean;
  rerank_method?: 'openai' | 'cross_encoder';
}

export interface SearchResultSchema {
  video_id: string;
  timestamp: number;
  score: number;
  segment_text: string;
  speaker: string;
  objects: string[];
  entities: string[];
  frame_path?: string;
  reranked_score?: number;
  rerank_reasoning?: string;
}

export interface SearchResponse {
  query: string;
  results: SearchResultSchema[];
  total_results: number;
  search_time: number;
  query_expansion?: {
    original_query: string;
    expanded_queries: string[];
    reasoning: string;
  };
  reranked_results?: RerankedResult[];
}

export interface RerankedResult {
  original_result: SearchResultSchema;
  reranked_score: number;
  reasoning: string;
}

export interface ClipGenerationRequest {
  video_id: string;
  query: string;
  start_time?: number;
  end_time?: number;
  padding?: number;
  smart_merge?: boolean;
  merge_mode?: 'loose' | 'strict' | 'llm' | 'hybrid';
}

export interface ClipGenerationResponse {
  success: boolean;
  clip_id: string;
  video_id: string;
  start_time: number;
  end_time: number;
  duration: number;
  clip_path: string;
  thumbnail_path?: string;
  description: string;
  query: string;
  message: string;
}

export interface VideoInfo {
  video_id: string;
  original_path: string;
  duration: number;
  frames_count: number;
  segments_count: number;
  speakers: string[];
  objects_detected: string[];
  entities: Record<string, string[]>;
  indexed_at: string;
  status: string;
}

export interface VisualQARequest {
  video_id: string;
  question: string;
  timestamp: number;
  context?: string;
}

export interface VisualQAResponse {
  success: boolean;
  question: string;
  answer: string;
  confidence: number;
  frame_path: string;
  timestamp: number;
  message: string;
}

export interface MontageRequest {
  video_id: string;
  theme: string;
  queries: string[];
  clips_per_query?: number;
  smart_merge_mode?: string;
}

export interface MontageResponse {
  success: boolean;
  montage_id: string;
  theme: string;
  clips_count: number;
  total_duration: number;
  montage_path: string;
  message: string;
}

export interface SystemStats {
  success: boolean;
  stats: Record<string, unknown>;
  message: string;
}

// Helper function for API calls
async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${BACKEND_BASE_URL}${endpoint}`;
  
  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const response = await fetch(url, { ...defaultOptions, ...options });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API call failed: ${response.status} ${response.statusText} - ${errorText}`);
  }
  
  return response.json();
}

// Video Search API
export const videoSearchApi = {
  // Video Indexing
  indexVideo: async (file: File, options: VideoIndexRequest = {}): Promise<VideoIndexResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    
    if (options.video_id) formData.append('video_id', options.video_id);
    formData.append('frame_interval', (options.frame_interval || 1.0).toString());
    formData.append('enable_diarization', (options.enable_diarization !== false).toString());
    
    const response = await fetch(`${BACKEND_BASE_URL}/search/index`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Video indexing failed: ${response.status} ${response.statusText} - ${errorText}`);
    }
    
    return response.json();
  },

  // Video Management
  listVideos: async (): Promise<VideoInfo[]> => {
    return apiCall<VideoInfo[]>('/search/videos');
  },

  getVideoInfo: async (videoId: string): Promise<VideoInfo> => {
    return apiCall<VideoInfo>(`/search/videos/${videoId}`);
  },

  deleteVideo: async (videoId: string): Promise<{ success: boolean; message: string }> => {
    return apiCall<{ success: boolean; message: string }>(`/search/videos/${videoId}`, {
      method: 'DELETE',
    });
  },

  // Search
  search: async (request: SearchRequest): Promise<SearchResponse> => {
    return apiCall<SearchResponse>('/search/search', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  // Clip Generation
  generateClip: async (request: ClipGenerationRequest): Promise<ClipGenerationResponse> => {
    return apiCall<ClipGenerationResponse>('/search/clips/generate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  downloadClip: async (clipId: string): Promise<Blob> => {
    const response = await fetch(`${BACKEND_BASE_URL}/search/clips/${clipId}`);
    
    if (!response.ok) {
      throw new Error(`Clip download failed: ${response.status} ${response.statusText}`);
    }
    
    return response.blob();
  },

  getClipThumbnail: async (clipId: string): Promise<string> => {
    const response = await fetch(`${BACKEND_BASE_URL}/search/clips/${clipId}/thumbnail`);
    
    if (!response.ok) {
      throw new Error(`Thumbnail download failed: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  deleteClip: async (clipId: string): Promise<{ success: boolean; message: string }> => {
    return apiCall<{ success: boolean; message: string }>(`/search/clips/${clipId}`, {
      method: 'DELETE',
    });
  },

  // Visual QA
  visualQA: async (request: VisualQARequest): Promise<VisualQAResponse> => {
    return apiCall<VisualQAResponse>('/search/visual-qa', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  // Multimodal RAG
  multimodalRAG: async (videoId: string, query: string, topK: number = 5): Promise<Record<string, unknown>> => {
    const formData = new FormData();
    formData.append('video_id', videoId);
    formData.append('query', query);
    formData.append('top_k', topK.toString());
    
    const response = await fetch(`${BACKEND_BASE_URL}/search/multimodal-rag`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Multimodal RAG failed: ${response.status} ${response.statusText} - ${errorText}`);
    }
    
    return response.json();
  },

  // Montage Creation
  createMontage: async (request: MontageRequest): Promise<MontageResponse> => {
    return apiCall<MontageResponse>('/search/montages/create', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  downloadMontage: async (montageId: string): Promise<Blob> => {
    const response = await fetch(`${BACKEND_BASE_URL}/search/montages/${montageId}`);
    
    if (!response.ok) {
      throw new Error(`Montage download failed: ${response.status} ${response.statusText}`);
    }
    
    return response.blob();
  },

  // System Management
  getSystemStats: async (): Promise<SystemStats> => {
    return apiCall<SystemStats>('/search/stats');
  },

  rebuildIndices: async (videoId: string): Promise<{ success: boolean; message: string }> => {
    return apiCall<{ success: boolean; message: string }>(`/search/rebuild-indices/${videoId}`, {
      method: 'POST',
    });
  },

  // Health Check
  healthCheck: async (): Promise<{ status: string; timestamp: string; version: string }> => {
    return apiCall<{ status: string; timestamp: string; version: string }>('/health');
  },
};

// Utility functions for converting between frontend and backend types
export const convertBackendVideoToFrontend = (backendVideo: VideoInfo): Video => {
  return {
    id: backendVideo.video_id,
    title: backendVideo.original_path.split('/').pop() || backendVideo.video_id,
    duration: backendVideo.duration,
    thumbnail: '', // Backend doesn't provide thumbnails yet
    processingStatus: backendVideo.status.toLowerCase() as 'pending' | 'processing' | 'completed' | 'failed',
    metadata: {
      speakers: backendVideo.speakers,
      objects: backendVideo.objects_detected,
      frameCount: backendVideo.frames_count,
      segmentCount: backendVideo.segments_count,
    },
    createdAt: backendVideo.indexed_at,
    isRealVideo: true,
  };
};

export const convertBackendSearchResultToFrontend = (backendResult: SearchResultSchema): SearchResult => {
  return {
    type: 'segment',
    videoId: backendResult.video_id,
    startTime: backendResult.timestamp,
    endTime: backendResult.timestamp + 10, // Approximate end time
    text: backendResult.segment_text,
    speaker: backendResult.speaker,
    objects: backendResult.objects,
    relevance: backendResult.reranked_score || backendResult.score,
  };
}; 